import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { List, Map } from 'immutable';

import Popup from '../../helpers/Popup';
import Button from '../../helpers/Button';
import FormErrors from '../../helpers/FormErrors';
import { UserInput } from '../../User/UserInput';
import { UserDashboardTitleList } from '../../User/UserDashboardTitleList';
import { FirefliesApiKey } from '../../User/FirefliesApiKey';
import style from './style.module.css';

/**
 * Popup with the form to edit the current user's profile.
 *
 * @param {object} props Props.
 * @param {Immutable.Map} props.selectedUser Current user's form data.
 * @param {Immutable.Map} props.formError Errors of the form fields.
 * @param {Immutable.List} props.dashboardUserTitles Title categories.
 * @param {boolean} props.loading Whether the user is loading or saving.
 * @param {boolean} props.isSaveDisabled Whether the Save button is disabled.
 * @param {Immutable.List} props.commonErrors API errors not related to a form field.
 * @param {Function} props.onFormChange Handle form changing.
 * @param {Function} props.onSave Handle the Save button click.
 * @param {Function} props.onCancel Handle the Cancel button click.
 */
const ProfilePopupComponent = ({
  selectedUser,
  formError,
  dashboardUserTitles,
  loading,
  isSaveDisabled,
  commonErrors,
  onFormChange,
  onSave,
  onCancel,
}) => {
  const inputProps = { selectedUser, formMode: 'edit', formError };
  // The user can't change these fields of their own profile, they are shown disabled.
  const readOnlyProps = { ...inputProps, formMode: 'view' };
  const shareFirefliesMeetings = selectedUser.get('shareFirefliesMeetings', false);

  const footer = (
    <>
      <Button className="btn-primary" disabled={isSaveDisabled} onClick={onSave}>
        Save
      </Button>
      <Button onClick={onCancel}>Cancel</Button>
    </>
  );

  return (
    <Popup className={style.host} footer={footer} header="Profile" id="profile" loading={loading}>
      <form className={style.form} onChange={onFormChange}>
        <fieldset className={style.section}>
          <legend>Basic User Info</legend>
          <div className={style.fields}>
            <UserInput {...inputProps} autoComplete="off" label="User Name" name="userName" />
            <UserInput {...inputProps} label="First Name" name="firstName" />
            <UserInput {...inputProps} label="Last Name" name="lastName" />
            <UserInput {...inputProps} label="Email" name="email" />
            <UserInput {...readOnlyProps} label="Title" name="title" />
            <UserDashboardTitleList {...readOnlyProps} dashboardUserTitles={dashboardUserTitles} />
          </div>
        </fieldset>

        <div className={style.sectionsRow}>
          <fieldset className={style.section}>
            <legend>Contact Info</legend>
            <div className={style.stackFields}>
              <div className={style.phone}>
                <UserInput {...readOnlyProps} label="Office Phone" name="officePhone" />
                <UserInput {...readOnlyProps} label="Extension" name="officePhoneExt" />
              </div>
              <UserInput {...readOnlyProps} label="Direct Dial" name="directDial" />
              <UserInput {...readOnlyProps} label="Cell Phone" name="cellPhone" />
            </div>
          </fieldset>

          <fieldset className={style.section}>
            <legend>Account Security</legend>
            <div className={style.stackFields}>
              <UserInput {...inputProps} autoComplete="new-password" label="Password" name="password" type="password" />
              <UserInput
                {...inputProps}
                autoComplete="new-password"
                label="Re-Enter Password"
                name="rePassword"
                type="password"
              />
            </div>
          </fieldset>
        </div>

        <fieldset className={style.section}>
          <legend>Integrations</legend>
          <div className={style.integrationFields}>
            <FirefliesApiKey {...inputProps} compact />
            <UserInput {...inputProps} label="Dialpad User ID" name="dialpadUserId" />
          </div>
          <fieldset className={style.subgroup}>
            <legend>Meeting Privacy</legend>
            <label className={style.radio} htmlFor="profileShareFirefliesMeetingsOnlyMe">
              <input
                checked={!shareFirefliesMeetings}
                id="profileShareFirefliesMeetingsOnlyMe"
                name="shareFirefliesMeetings"
                type="radio"
                value="false"
              />
              Only Me
            </label>
            <label className={style.radio} htmlFor="profileShareFirefliesMeetingsShare">
              <input
                checked={shareFirefliesMeetings}
                id="profileShareFirefliesMeetingsShare"
                name="shareFirefliesMeetings"
                type="radio"
                value="true"
              />
              Share with Participants/Teammates
            </label>
          </fieldset>
        </fieldset>
      </form>
      <FormErrors errors={commonErrors} />
    </Popup>
  );
};

ProfilePopupComponent.propTypes = {
  commonErrors: PropTypes.instanceOf(List).isRequired,
  dashboardUserTitles: PropTypes.instanceOf(List).isRequired,
  formError: PropTypes.instanceOf(Map).isRequired,
  isSaveDisabled: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onFormChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  selectedUser: PropTypes.instanceOf(Map).isRequired,
};

export const ProfilePopup = memo(ProfilePopupComponent);
